import { useIntl } from '@/intl'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useFilters, useResults } from '@/scopes/search/search/domain/hooks'
import { Filters } from '@/scopes/search/search/pages/SearchPage/components/Filters'
import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle, Sheet, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { useState } from 'react'
import { css, styled } from 'styled-components'
import { useCustomDesktop } from './useCustomDesktop'

const FiltersButton = styled(Button)<{ $splitDetailsEnabled: boolean, $isLandingPage?: boolean }>`
  display: flex;
  position: fixed;
  bottom: ${space('primitives_space_24')};
  z-index: 50; // QuickSearchBar has z-index equals 50
  width: calc(100% - (2 * ${space('primitives_space_16')}));
  box-shadow: ${tok('primitives_box_shadows_level_3')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: calc(100% - (2 * ${space('primitives_space_32')}));
  }

  ${({ $isLandingPage }) => $isLandingPage && css`
    width: calc(100% - (2 * ${space('primitives_space_28')}));

    @media ${mqMin('primitives_breakpoint_m_min')} {
      width: calc(100% - (2 * ${space('primitives_space_44')}));
    }

    @media (min-width: 840px) {
      width: calc(100% - (2 * ${space('primitives_space_56')}));
    }
  `}

  ${({ $splitDetailsEnabled }) =>
    $splitDetailsEnabled
      ? css`
          @media (min-width: 1280px) {
            display: none;
          }
        `
      : css`
          @media ${mqMin('primitives_breakpoint_l_min')} {
            display: none;
          }
        `}
`

export function FiltersModal({ isLandingPage }: { isLandingPage?: boolean }) {
  const intl = useIntl()
  const { isFetching } = useResults()
  const { resetOptionalFilters } = useFilters()
  const [open, setOpen] = useState(false)
  const { productCardSplitDetails } = useOptimizelyFlagContext()
  const { isMobile } = useBreakpoints()
  const isCustomDesktop = useCustomDesktop()

  const shouldShowFiltersModal = !isCustomDesktop

  if (!shouldShowFiltersModal) {
    return null
  }

  return (
    <>
      <Sheet.Root open={open} onOpenChange={setOpen}>
        <Sheet.Trigger>
          <FiltersButton variant="secondary" fullWidth $splitDetailsEnabled={productCardSplitDetails} $isLandingPage={isLandingPage}>
            <Button.Icon glyph="Sliders" />
            {intl.formatMessage({ id: 'search_open_filters' })}
          </FiltersButton>
        </Sheet.Trigger>
        <Sheet.Body side={isMobile ? 'bottom' : 'right'}>
          <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={intl.formatMessage({ id: 'search_open_filters' })}>
            <Button
              variant="transparentPrimary"
              size="M"
              onClick={(event) => {
                resetOptionalFilters()
                setOpen(false)
                event.preventDefault()
              }}
            >
              {intl.formatMessage({ id: 'search_reset_filters_sheet_header' })}
            </Button>
          </Sheet.Header>
          <Sheet.Content>
            <Filters />
          </Sheet.Content>
          <Sheet.Footer>
            <Button variant="primary" fullWidth disabled={isFetching} onClick={() => { setOpen(false) }}>
              {isFetching && <IndeterminateProgressCircle />}
              {intl.formatMessage({ id: 'search_apply_filters' })}
            </Button>
          </Sheet.Footer>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}
